import { useMemo } from "react";

import type { IconName } from "@similarweb/icons";

import { signalTypeMap } from "app/pages/sales-intelligence/domain/signals/types/signal";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import { useSignalsManagementViewModel } from "app/pages/sales-intelligence/sub-modules/signals/hooks/useSignalsManagementViewModel";
import type SignalTypeWithIcon from "app/pages/sales-intelligence/sub-modules/signals/models/view/SignalTypeWithIcon";
import signalsService from "app/pages/sales-intelligence/sub-modules/signals/services/signalsService";

export type AccountSignalTypeEntry = SignalTypeWithIcon & {
    isComingSoon: boolean;
};

export const ensureIntentSignalType = (
    types: readonly AccountSignalTypeEntry[],
    intentIcon: IconName,
): readonly AccountSignalTypeEntry[] => {
    if (types.some(({ type }) => type === signalTypeMap.intent)) {
        return types;
    }

    return [
        {
            type: signalTypeMap.intent,
            icon: intentIcon,
            isComingSoon: false,
        },
        ...types,
    ];
};

export const useAccountSignalsPageTypes = () => {
    const viewModel = useSignalsManagementViewModel();
    const isHighLevelAccount = salesSettingsHelper.isHighLevelAccount();

    const signalTypes = useMemo((): readonly AccountSignalTypeEntry[] => {
        const orderedTypes: readonly AccountSignalTypeEntry[] = signalsService.orderedSignalTypes.map((type) => ({
            type,
            icon: signalsService.getIconForSignalType(type),
            isComingSoon: signalsService.getSignalTypeLifecycleState(type) === "coming-soon",
        }));

        return ensureIntentSignalType(orderedTypes, signalsService.getIconForSignalType(signalTypeMap.intent));
    }, []);

    const scrollableTypes = useMemo(() => signalTypes.filter(({ isComingSoon }) => !isComingSoon), [signalTypes]);

    return {
        displayTypes: signalTypes,
        scrollableTypes,
        isHighLevelAccount,
        viewModel,
    };
};
